// WebSocket and EventSource. We replace the constructors with subclasses, so
// instanceof checks, static constants and the prototype all keep working.

import { absoluteUrl, messageSummary, shortUrl } from '../net-utils.js';

const MESSAGE_PREVIEW = 2000;

export function installWebSocket(ctx, win = window) {
  const Native = win.WebSocket;
  if (typeof Native !== 'function') return;

  const record = (level, title, detail) => {
    const clean = ctx.sanitise('websocket', detail);
    if (clean) ctx.push('websocket', level, title, clean);
  };

  class WebSocket extends Native {
    constructor(url, protocols) {
      if (protocols === undefined) super(url);
      else super(url, protocols);
      let href;
      try {
        href = absoluteUrl(url);
      } catch {
        href = String(url);
      }
      if (ctx.isOwnUrl(href)) return;
      const label = shortUrl(ctx.redact.url(href));
      const safeUrl = ctx.redact.url(href);
      Object.defineProperty(this, '__adidoBugs', { value: { label, url: safeUrl }, enumerable: false });
      record('info', `WebSocket connecting ${label}`, { url: safeUrl, event: 'connecting' });
      this.addEventListener('open', () =>
        record('info', `WebSocket open ${label}`, {
          url: safeUrl,
          event: 'open',
          protocol: this.protocol || undefined,
        }),
      );
      this.addEventListener('error', () =>
        record('error', `WebSocket error ${label}`, { url: safeUrl, event: 'error' }),
      );
      this.addEventListener('close', (event) =>
        record(event.wasClean ? 'info' : 'warn', `WebSocket closed ${label} (${event.code})`, {
          url: safeUrl,
          event: 'close',
          code: event.code,
          reason: event.reason || undefined,
          wasClean: event.wasClean,
        }),
      );
      this.addEventListener('message', (event) =>
        record('info', `↓ ${label}`, {
          url: safeUrl,
          event: 'message',
          direction: 'received',
          ...messageSummary(event.data, MESSAGE_PREVIEW),
        }),
      );
    }

    send(data) {
      const meta = this.__adidoBugs;
      if (meta) {
        try {
          record('info', `↑ ${meta.label}`, {
            url: meta.url,
            event: 'message',
            direction: 'sent',
            ...messageSummary(data, MESSAGE_PREVIEW),
          });
        } catch {
          // ignore
        }
      }
      return super.send(data);
    }
  }

  win.WebSocket = WebSocket;
}

export function installEventSource(ctx, win = window) {
  const Native = win.EventSource;
  if (typeof Native !== 'function') return;

  class EventSource extends Native {
    constructor(url, init) {
      super(url, init);
      const href = absoluteUrl(url);
      if (ctx.isOwnUrl(href)) return;
      const label = shortUrl(ctx.redact.url(href));
      const safeUrl = ctx.redact.url(href);
      const record = (level, title, detail) => {
        const clean = ctx.sanitise('websocket', detail);
        if (clean) ctx.push('sse', level, title, clean);
      };
      this.addEventListener('open', () => record('info', `EventSource open ${label}`, { url: safeUrl, event: 'open' }));
      this.addEventListener('error', () =>
        record('warn', `EventSource error ${label}`, { url: safeUrl, event: 'error', readyState: this.readyState }),
      );
      this.addEventListener('message', (event) =>
        record('info', `↓ ${label}`, {
          url: safeUrl,
          event: 'message',
          lastEventId: event.lastEventId || undefined,
          ...messageSummary(event.data, MESSAGE_PREVIEW),
        }),
      );
    }
  }

  win.EventSource = EventSource;
}
